import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { coachJourney, techRoadmap, toggleRoadmapTopic, skillGap, skillGapRefresh, learningResources } from "@/lib/api";
import RoadmapPage from "@/pages/app/RoadmapPage";
import { MyCourses } from "@/components/wishire/CourseCards";
import {
  CheckCircle2, Circle, ArrowRight, CalendarClock, Loader2, Check, Youtube, Star, BookOpen, Zap, RefreshCw,
  Linkedin, Compass, Sparkles, MessageSquare, PlayCircle, ExternalLink,
} from "lucide-react";

/* My Coaching Plan — the ONE page a student needs: where they are, what to do
   this week, the 12-week plan with courses and free resources attached, a skill
   snapshot, LinkedIn as a step, and "find my fit" for those who don't know
   their role yet. (Replaces the old Coach / Skill Gaps / Learning / My Plan /
   LinkedIn tabs.) */

const STAGE_TONE = {
  setup: "bg-stone-100 text-stone-700 border-stone-200",
  baseline: "bg-blue-50 text-blue-700 border-blue-200",
  build: "bg-amber-50 text-amber-700 border-amber-200",
  drive_season: "bg-[#C2410C]/10 text-[#C2410C] border-[#C2410C]/30",
  interview_ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  placed: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function CoachPlanPage() {
  const { user } = useAuth();
  const [journey, setJourney] = useState(null);
  const [plan, setPlan] = useState(undefined);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    coachJourney().then(({ data }) => setJourney(data)).catch(() => {});
    techRoadmap().then(({ data }) => setPlan(data)).catch(() => setPlan(null));
  }, []);

  const tick = async (id) => {
    setBusy(id);
    try { const { data } = await toggleRoadmapTopic(id); setPlan(data); setReloadKey((k) => k + 1); } catch (_) {} finally { setBusy(null); }
  };

  const first = (user?.name || "").split(" ")[0];
  const roleBroad = !user?.target_role || /any|not sure|fresher role|don.t know/i.test(user.target_role);
  const thisWeek = plan ? plan.phases.flatMap((p) => p.topics.filter((t) => !t.done).map((t) => ({ ...t, phase: p.title }))).slice(0, 3) : [];

  return (
    <div className="container-x py-10 md:py-14" data-testid="coach-plan">
      {/* Header: where you are */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="overline">My coaching plan</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">Your plan to get placed{first ? `, ${first}` : ""}.</h1>
          <p className="mt-2 text-stone-600 max-w-xl">One page. What to do this week, the full 12-week plan, courses and free resources for each step, and a coach to ask anything.</p>
        </div>
        {journey && (
          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${STAGE_TONE[journey.stage] || STAGE_TONE.build}`} data-testid="coach-stage">{journey.stage_label}</span>
            <span className="text-sm text-stone-500">{journey.milestones_done}/{journey.milestones.length} milestones · Score {journey.score}</span>
            <div className="flex items-center gap-1.5">
              {journey.milestones.map((m) => <Link key={m.key} to={m.path} title={m.label}>{m.done ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Circle size={16} className="text-stone-300 hover:text-stone-500" />}</Link>)}
            </div>
          </div>
        )}
      </div>

      {/* Row 1: this week + next best moves */}
      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 rounded-3xl bg-stone-900 text-stone-50 p-6 md:p-7 wsh-grain relative overflow-hidden" data-testid="this-week">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(500px 300px at 90% 10%, rgba(194,65,12,0.5), transparent)" }} />
          <div className="relative">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>This week</p>
              {plan && <p className="text-xs text-stone-400">{plan.progress.completed}/{plan.progress.total} steps done · ~{plan.progress.hours_left}h left</p>}
            </div>
            {plan === undefined ? <p className="mt-4 text-sm text-stone-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Building your plan… (first time takes ~20s)</p>
             : plan === null ? <p className="mt-4 text-sm text-stone-400">Couldn't load your plan — refresh to retry.</p>
             : thisWeek.length === 0 ? <p className="mt-4 text-emerald-300 inline-flex items-center gap-2"><Check size={16} /> Every step is done. You're ready — go apply.</p>
             : (
              <div className="mt-4 space-y-2.5">
                {thisWeek.map((t) => (
                  <div key={t.topic_id} className="flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
                    <button onClick={() => tick(t.topic_id)} disabled={busy === t.topic_id} aria-label="Mark done" className="shrink-0 mt-0.5 w-6 h-6 rounded-lg border-2 border-stone-500 hover:border-amber-300 flex items-center justify-center">
                      {busy === t.topic_id ? <Loader2 size={12} className="animate-spin text-stone-400" /> : null}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-stone-50">{t.title}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{t.phase} · ~{t.est_hours}h{t.detail ? ` · ${t.detail}` : ""}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <a href={t.resource_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-white/10 text-stone-200 hover:bg-white/20"><Youtube size={11} className="text-red-400" /> Find tutorial</a>
                        {t.cta && <Link to={t.cta.path} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-white/10 text-stone-200 hover:bg-white/20">{t.cta.label} <ArrowRight size={10} /></Link>}
                        {(t.courses || []).map((c) => <a key={c.course_id} href={c.url || "#courses"} target={c.url ? "_blank" : undefined} rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-amber-300/15 text-amber-200 hover:bg-amber-300/25">{c.required ? <Star size={10} className="fill-amber-300 text-amber-300" /> : <BookOpen size={10} />} {c.title}</a>)}
                      </div>
                    </div>
                  </div>
                ))}
                <a href="#full-plan" className="inline-flex items-center gap-1 text-sm text-amber-300 hover:text-amber-200 mt-1">See the full 12-week plan <ArrowRight size={14} /></a>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-3xl border border-stone-200 bg-white p-5" data-testid="next-moves">
            <p className="overline">Next best moves</p>
            {!journey ? <p className="mt-3 text-sm text-stone-400">Loading…</p> : (
              <div className="mt-3 space-y-2">
                {(journey.next_actions || []).map((a, i) => (
                  <Link key={i} to={a.path} data-testid={`coach-action-${i}`} className="group flex items-start gap-3 rounded-2xl border border-stone-200 px-3.5 py-2.5 hover:border-[#C2410C] transition-colors">
                    <ArrowRight size={14} className="mt-0.5 shrink-0 text-[#C2410C] group-hover:translate-x-0.5 transition-transform" />
                    <span><span className="block text-sm font-medium text-stone-900">{a.label}</span><span className="block text-xs text-stone-500 mt-0.5">{a.why}</span></span>
                  </Link>
                ))}
                {journey.deadlines?.length > 0 && (
                  <p className="pt-1 text-xs text-stone-500 flex items-center gap-1.5 flex-wrap"><CalendarClock size={13} className="text-[#C2410C]" />
                    {journey.deadlines.map((d, i) => <span key={d.drive_id}><b className="text-stone-700">{d.company}</b> closes {new Date(d.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{i < journey.deadlines.length - 1 ? " · " : ""}</span>)}
                  </p>
                )}
              </div>
            )}
          </div>

          <Link to="/app/coach?tab=ask" className="block rounded-3xl border border-stone-200 bg-white p-5 hover:border-stone-900 transition-colors group" data-testid="ask-coach-card">
            <p className="overline">Ask your coach</p>
            <p className="text-sm text-stone-700 mt-2">It can see your score, resume, interviews and campus drives — ask "what should I do this week?" or "how do I prepare for Infosys?"</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-stone-900 group-hover:text-[#C2410C]"><MessageSquare size={14} /> Open chat <ArrowRight size={14} /></span>
          </Link>
        </div>
      </div>

      {/* Find my fit — prominent when the role is unknown */}
      <Link to="/app/coach?tab=ask&mode=fit" data-testid="fit-card"
        className={`mt-5 block rounded-3xl border-2 p-5 md:p-6 transition-colors group ${roleBroad ? "border-[#C2410C]/40 bg-amber-50/60 hover:border-[#C2410C]" : "border-stone-200 bg-white hover:border-stone-900"}`}>
        <div className="flex items-start gap-4">
          <span className={`shrink-0 p-3 rounded-2xl ${roleBroad ? "bg-[#C2410C] text-white" : "bg-stone-100 text-stone-700"}`}><Compass size={20} /></span>
          <div className="flex-1">
            <p className="font-display text-xl">{roleBroad ? "Not sure which role suits you? Find your fit in 2 minutes." : `Aiming for ${user.target_role}. Want a second opinion?`}</p>
            <p className="text-sm text-stone-600 mt-1">Six quick questions → three realistic first-job roles for your course, with why, what to prepare, typical pay and which campus drives match.</p>
          </div>
          <span className="shrink-0 self-center text-sm font-medium text-stone-900 group-hover:text-[#C2410C] inline-flex items-center gap-1">Start <ArrowRight size={14} /></span>
        </div>
      </Link>

      {/* Courses: tracker, catalogue filters, recommended first, completed list */}
      <div className="mt-10" id="courses"><MyCourses /></div>

      {/* Full plan */}
      <div id="full-plan" className="mt-2">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-2">
          <div>
            <span className="overline">The full plan</span>
            <h2 className="font-display text-3xl mt-1">12 weeks, in the order recruiters screen freshers.</h2>
            <p className="text-sm text-stone-600 mt-1 max-w-2xl">Aptitude first, then your course's fundamentals, problem solving, resume, interviews. Built for the companies coming to your campus — no role needed. Each step has a tutorial, a Wishire tool and, where it helps, a course.</p>
          </div>
        </div>
        <RoadmapPage embedded reloadKey={reloadKey} onData={setPlan} />
      </div>

      {/* Skill snapshot + free resources */}
      <SkillSnapshot />

      {/* LinkedIn as a step */}
      <div className="mt-8 rounded-3xl border border-stone-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid="linkedin-step">
        <div className="flex items-center gap-4">
          <span className="p-3 rounded-2xl bg-blue-50 text-[#0A66C2]"><Linkedin size={20} /></span>
          <div>
            <p className="font-display text-xl">LinkedIn{user?.linkedin_score != null ? ` · ${user.linkedin_score}/100` : ""}</p>
            <p className="text-sm text-stone-600">{user?.linkedin_score != null ? "Recruiters check it before they call. Keep polishing the sections the audit flagged." : "10% of your Wishire Score. Paste your URL, get a score and section-by-section rewrites."}</p>
          </div>
        </div>
        <Link to="/app/linkedin" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-stone-50 text-sm font-medium hover:bg-[#C2410C]">{user?.linkedin_score != null ? "Polish LinkedIn" : "Score my LinkedIn"} <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
}

function SkillSnapshot() {
  const [gap, setGap] = useState(undefined);
  const [learn, setLearn] = useState(undefined);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    skillGap().then(({ data }) => setGap(data)).catch(() => setGap(null));
    learningResources().then(({ data }) => setLearn(data)).catch(() => setLearn(null));
  }, []);
  const refresh = async () => { setRefreshing(true); try { const { data } = await skillGapRefresh(); setGap(data); } catch (_) {} finally { setRefreshing(false); } };
  const resources = (learn?.resources || []).slice(0, 6);
  return (
    <div id="skills" className="mt-10" data-testid="skill-snapshot">
      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 rounded-3xl border border-stone-200 bg-white p-6">
          <div className="flex items-center justify-between gap-2">
            <p className="overline">Skill snapshot</p>
            {gap && <button onClick={refresh} disabled={refreshing} className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">{refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Re-analyse</button>}
          </div>
          {gap === undefined ? <p className="mt-3 text-sm text-stone-400 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Analysing…</p>
           : gap === null ? <p className="mt-3 text-sm text-stone-500">Add a résumé or skills on your profile to see where you stand.</p> : (
            <>
              <div className="mt-3 flex items-end gap-3">
                <p className="font-display text-5xl">{gap.coverage_percent || 0}<span className="text-2xl text-stone-400">%</span></p>
                <p className="text-sm text-stone-600 pb-2">of what <b>{gap.target_role}</b> needs · {gap.verdict}</p>
              </div>
              <div className="mt-2 h-2 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${gap.coverage_percent || 0}%` }} /></div>
              {gap.top_skill_to_learn_next && <p className="mt-3 text-sm"><Zap size={13} className="inline text-[#C2410C] mr-1" />Learn next: <b>{gap.top_skill_to_learn_next}</b></p>}
              {(gap.missing_must_haves || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Must-have gaps</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">{gap.missing_must_haves.map((s) => <span key={s} className="px-2.5 py-1 rounded-full text-xs bg-red-50 border border-red-100 text-red-700">{s}</span>)}</div>
                </div>
              )}
              <button onClick={() => setOpen((o) => !o)} className="mt-3 text-xs text-stone-500 hover:text-stone-900 underline">{open ? "Hide" : "Show"} skills you already have</button>
              {open && <div className="mt-2 flex flex-wrap gap-1.5">{(gap.required_skills || []).filter((s) => s.candidate_has).map((s) => <span key={s.skill} className="px-2.5 py-1 rounded-full text-xs bg-emerald-50 border border-emerald-100 text-emerald-700">{s.skill}</span>)}</div>}
            </>
          )}
        </div>
        <div className="lg:col-span-3 rounded-3xl border border-stone-200 bg-white p-6" id="resources">
          <p className="overline">Free resources for your gaps</p>
          {learn === undefined ? <p className="mt-3 text-sm text-stone-400 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Picking the best free tutorials…</p>
           : resources.length === 0 ? <p className="mt-3 text-sm text-stone-500">Resources appear once your skill gaps are known.</p> : (
            <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
              {resources.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noreferrer" className="group flex items-start gap-3 rounded-2xl border border-stone-200 px-3.5 py-3 hover:border-stone-900 transition-colors">
                  <span className="shrink-0 mt-0.5 text-red-500">{r.type === "playlist" ? <PlayCircle size={16} /> : r.type === "course" ? <BookOpen size={16} className="text-blue-600" /> : <Youtube size={16} />}</span>
                  <span className="min-w-0"><span className="block text-sm font-medium text-stone-900 leading-snug">{r.title}</span><span className="block text-xs text-stone-500 mt-0.5">{r.skill}{r.channel ? ` · ${r.channel}` : ""}</span></span>
                  <ExternalLink size={12} className="ml-auto shrink-0 text-stone-300 group-hover:text-stone-600 mt-1" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
